import { SegmentStrategy } from "../rules/intoSegments";
import { RuleSet } from "../rules/ruleSet";

// https://en.wikipedia.org/wiki/Old_English
// consider https://ealdaenglisc.wordpress.com/old-english-lessons/lesson-0-alphabet-and-pronunciation/
export const oldEnglishRules: RuleSet = {
  preprocess: ['lowerCase', 'cleanPunctuation'],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.Ipa,

  // first convert special words,
  // then vowels, so they're in IPA so we understand their features,
  // finally consonants, which depend on surrounding features
  rules: [
    // special words
    { from: 'cyning', to: 'ˈkɪnɪng', previous: ['#word'], next: ['#word'] }, // (king)
    { from: 'cene', to: 'ˈkene', previous: ['#word'], next: ['#word'] }, // (keen)
    { from: 'byrgan', to: 'bɪrjɑn', previous: ['#word'], next: ['#word'] },
    { from: 'fylgan', to: 'fɪljɑn', previous: ['#word'], next: ['#word'] },
    { from: 'sprengan', to: 'sprend͡ʒɑn', previous: ['#word'], next: ['#word'] },
    { from: 'scól', to: 'skoːl', previous: ['#word'], next: ['#word'] }, // (school)
    { from: 'áscian', to: 'ɑːskɪɑn', previous: ['#word'], next: ['#word'] }, // (ask)
    { from: 'scottas', to: 'skottɑs', previous: ['#word'], next: ['#word'] },

    // different transcriptions may use different diacritics for long vowels, e.g., á ā
    { from: 'ig', to: 'iː' },
    { from: 'ēa', to: 'æːa' },
    { from: 'ǣ', to: 'æː' }, { from: 'ǽ', to: 'æː' },
    { from: 'æ', to: 'æ' },
    { from: 'ā', to: 'ɑː' }, { from: 'á', to: 'ɑː' },
    { from: 'a', to: 'ɑ' },
    { from: 'ē', to: 'eː' }, { from: 'é', to: 'eː' },
    { from: 'e', to: 'e' },
    { from: 'ī', to: 'iː' }, { from: 'í', to: 'iː' }, // or ɪː
    { from: 'i', to: 'ɪ' },
    { from: 'ō', to: 'oː' }, { from: 'ó', to: 'oː' },
    { from: 'o', to: 'o' },
    { from: 'u', to: 'ʊ' },
    { from: 'ú', to: 'u' },
    { from: 'ū', to: 'uː' },
    { from: 'ȳ', to: 'yː' }, { from: 'ý', to: 'yː' },
    { from: 'y', to: 'y' },

    // now convert consonants, which may involve looking at vowel features,
    // unchanged consonants: b, d, l, m, n, p, r, t, w
    // rarely or never used: j, k, q (cw or cu), v, x (cs), z (ts)
    { from: 'cg', to: 'd͡ʒ' }, // ecg  (edge)
    { from: 'sc', to: 'ʃ' },
    { from: 'q', to: 'kw' },
    { from: 'ƿ', to: 'w' },
    { from: 'x', to: 'ks' },
    { from: 'z', to: 'ts' },

    // c -> k or t͡ʃ
    { from: 'c', to: 'k', previous: ['vowel', 'front'], next: ['#word'] }, // bæc (back) (after æ, e, i long or short))
    { from: 'c', to: 'k', previous: ['#word'], next: { not: ':vowel' } }, // cnáwan (know)
    { from: 'c', to: 't͡ʃ', previous: 'l' }, // ælc (each)
    { from: 'c', to: 't͡ʃ', previous: 'n' }, // þencan (think)
    // before a, o, u long or short
    { from: 'c', to: 'k', next: { or: ['/ɑ/', '/ɑː/'] } },
    { from: 'c', to: 'k', next: ['vowel', 'back'] },
    // before e, i, y long or short
    { from: 'c', to: 't͡ʃ', next: 'ɪ' }, // cild (child)
    { from: 'c', to: 't͡ʃ', next: ['vowel', 'front'] },
    // default
    { from: 'c', to: 'k' },

    // f -> f or v
    { from: 'ff', to: 'ff' },
    { from: 'f', to: 'f', previous: ['#word'] },
    { from: 'f', to: 'f', previous: ['voiceless'] },
    { from: 'f', to: 'f', next: ['voiceless'] },
    { from: 'f', to: 'v', previous: ['vowel'], next: ['vowel'] },
    { from: 'f', to: 'v', previous: ['vowel'], next: ['voiced'] },
    { from: 'f', to: 'v', previous: ['voiced'], next: ['vowel'] },

    // g -> g or j
    { from: 'gg', to: 'gg' }, // frogga (frog)
    { from: 'g', to: 'j', next: { or: ['/e/', '/eː/', '/ɪ/', '/iː/'] } },
    { from: 'g', to: 'j', previous: { or: ['/e/', '/eː/', '/ɪ/', '/iː/', '/æ/'] } },
    { from: 'g', to: 'ɣ', previous: ['#word'], next: { not: ':vowel' } }, // ɣlæd (glad)
    { from: 'g', to: 'g', next: 'ʊ' },
    { from: 'g', to: 'g', next: ['vowel', 'back'] }, // ɑ, o, u long or short
    { from: 'g', to: 'g', next: { or: ['/y/', '/yː/'] } },

    { from: 'hl', to: 'l\u030A' },  // voiceless
    { from: 'hn', to: 'n\u030A' },  // voiceless
    { from: 'hr', to: 'r\u030A' },  // voiceless
    { from: 'hw', to: 'w\u030A' },  // voiceless
    { from: 'h', to: 'h', previous: ['#word'] },
    { from: 'h', to: 'ɣ', previous: 'ʊ' },
    { from: 'h', to: 'ɣ', previous: ['vowel', 'back'] }, // ɑ, o, u long or short
    { from: 'h', to: 'ɣ', previous: { not: ':vowel' } },
    { from: 'h', to: 'x', previous: 'ɪ' },
    { from: 'h', to: 'x', previous: ['vowel', 'front'] },  // e, i, y

    // s -> s or z
    { from: 'ss', to: 'ss' },
    { from: 's', to: 's', previous: ['#word'] },
    { from: 's', to: 's', previous: ['voiceless'] },
    { from: 's', to: 's', next: ['voiceless'] },
    { from: 's', to: 'z', previous: ['vowel'], next: ['vowel'] },
    { from: 's', to: 'z', previous: ['vowel'], next: ['voiced'] },
    { from: 's', to: 'z', previous: ['voiced'], next: ['vowel'] },

    // þ ð -> θ or ð
    { from: 'þþ', to: 'θθ' },
    { from: 'þ', to: 'θ', previous: ['#word'] },
    { from: 'þ', to: 'θ', previous: ['voiceless'] },
    { from: 'þ', to: 'θ', next: ['voiceless'] },
    { from: 'þ', to: 'ð', previous: ['vowel'], next: ['vowel'] },
    { from: 'þ', to: 'ð', previous: ['vowel'], next: ['voiced'] },
    { from: 'þ', to: 'ð', previous: ['voiced'], next: ['vowel'] },
    { from: 'ðð', to: 'θθ' },
    { from: 'ð', to: 'θ', previous: ['#word'] },
    { from: 'ð', to: 'θ', previous: ['voiceless'] },
    { from: 'ð', to: 'θ', next: ['voiceless'] },
    { from: 'ð', to: 'ð', previous: ['vowel'], next: ['vowel'] },
    { from: 'ð', to: 'ð', previous: ['vowel'], next: ['voiced'] },
    { from: 'ð', to: 'ð', previous: ['voiced'], next: ['vowel'] },
    // default
    { from: 'þ', to: 'θ' },
  ],

  stressRule: {
    default: 1,
    // accent on 2nd syllable if _for, _jeC, or _jeːC
    toCheck: [{
      which: 2,
      pattern: {
        seq: ['#word', {
          or: [
            '/for/',
            { seq: ['/je/', ':consonant'] },
            { seq: ['/jeː/', ':consonant'] },
          ]
        },]
      }
    }]
  }
}
